import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save, RefreshCw, Type, FileText, Globe } from 'lucide-react';
import apiService from '../../services/api';

const GeneralSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    headerTitle: '',
    headerSubtitle: '',
    defaultLanguage: 'tr'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const data = await apiService.request('/admin/settings/app');
      const map = {};
      data.forEach(s => { map[s.key] = s.value; });
      setSettings({
        headerTitle: map.headerTitle || '',
        headerSubtitle: map.headerSubtitle || '',
        defaultLanguage: map.defaultLanguage || 'tr'
      });
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await apiService.request('/admin/settings/app', {
        method: 'PUT',
        body: JSON.stringify({
          settings: [
            { key: 'headerTitle', value: settings.headerTitle, description: 'Chat panel header başlığı' },
            { key: 'headerSubtitle', value: settings.headerSubtitle, description: 'Chat panel header alt başlığı' },
            { key: 'defaultLanguage', value: settings.defaultLanguage, description: 'Varsayılan dil (tr/en)' }
          ]
        })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert(t('admin.general.saveError') + ': ' + (err.message || t('admin.common.error')));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <RefreshCw size={24} color="#7c3aed" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>{t('admin.general.title')}</h3>
        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280' }}>
          {t('admin.general.subtitle')}
        </p>
      </div>

      {/* Header Settings Card */}
      <div style={{
        padding: '20px',
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Type size={18} color="#7c3aed" />
          <h4 style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>{t('admin.general.headerInfo')}</h4>
        </div>
        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px', marginTop: 0 }}>
          {t('admin.general.headerInfoDesc')}
        </p>

        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
            {t('admin.general.headerTitle')}
          </label>
          <input
            type="text"
            value={settings.headerTitle}
            onChange={(e) => setSettings({ ...settings, headerTitle: e.target.value })}
            placeholder={t('admin.general.headerTitlePlaceholder')}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>{t('admin.general.headerTitleHelp')}</span>
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
            {t('admin.general.headerSubtitle')}
          </label>
          <input
            type="text"
            value={settings.headerSubtitle}
            onChange={(e) => setSettings({ ...settings, headerSubtitle: e.target.value })}
            placeholder={t('admin.general.headerSubtitlePlaceholder')}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>{t('admin.general.headerSubtitleHelp')}</span>
        </div>

        {/* Preview */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: '#374151' }}>
            <FileText size={14} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
            {t('admin.general.preview')}
          </label>
          <div style={{
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            borderRadius: '8px',
            color: 'white'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600' }}>
              {settings.headerTitle || t('admin.general.previewDefaultName')}
            </div>
            {(settings.headerSubtitle) && (
              <div style={{ fontSize: '11px', opacity: 0.9, marginTop: '2px' }}>
                {settings.headerSubtitle}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Language Settings Card */}
      <div style={{
        padding: '20px',
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Globe size={18} color="#7c3aed" />
          <h4 style={{ margin: 0, fontWeight: '600', color: '#1f2937' }}>{t('admin.general.languageSettings')}</h4>
        </div>
        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px', marginTop: 0 }}>
          {t('admin.general.languageSettingsDesc')}
        </p>

        <div style={{ display: 'flex', gap: '12px' }}>
          {[
            { code: 'tr', label: 'Türkçe', flag: '\uD83C\uDDF9\uD83C\uDDF7' },
            { code: 'en', label: 'English', flag: '\uD83C\uDDFA\uD83C\uDDF8' }
          ].map(lang => (
            <button
              key={lang.code}
              type="button"
              onClick={() => setSettings({ ...settings, defaultLanguage: lang.code })}
              style={{
                flex: 1,
                padding: '12px 16px',
                borderRadius: '10px',
                border: settings.defaultLanguage === lang.code
                  ? '2px solid #7c3aed'
                  : '2px solid #e5e7eb',
                background: settings.defaultLanguage === lang.code
                  ? '#f5f3ff'
                  : 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.15s'
              }}
            >
              <span style={{ fontSize: '24px' }}>{lang.flag}</span>
              <div style={{ textAlign: 'left' }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: settings.defaultLanguage === lang.code ? '600' : '400',
                  color: settings.defaultLanguage === lang.code ? '#7c3aed' : '#374151'
                }}>
                  {lang.label}
                </div>
                {settings.defaultLanguage === lang.code && (
                  <div style={{ fontSize: '11px', color: '#7c3aed', marginTop: '2px' }}>{t('admin.general.defaultLabel')}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 24px',
            background: saving ? '#9ca3af' : '#7c3aed',
            color: 'white',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <Save size={16} />
          {saving ? t('admin.general.saving') : t('admin.general.save')}
        </button>
        {saved && (
          <span style={{ fontSize: '13px', color: '#10b981', fontWeight: '500' }}>
            {t('admin.general.saved')}
          </span>
        )}
      </div>
    </div>
  );
};

export default GeneralSettings;
